/** Shared review card bits used by the reviews home + the cycle detail page. */
import { Link } from 'react-router-dom';
import type { ReviewAssignmentSummary } from '@daaily/shared';

export const STATUS_LABEL: Record<string, string> = {
  assigned: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
  released: 'Shared',
  declined: 'Declined',
};

export function raterLabel(a: ReviewAssignmentSummary): string {
  const rel = a.relationship ?? 'self';
  const base = a.surveyName || (rel === 'self' ? 'Self-review' : 'Review');
  return rel === 'self' || rel === 'company' ? base : `${base} · ${a.subjectDisplayName}`;
}

export function isSurveyDone(a: ReviewAssignmentSummary): boolean {
  return a.status === 'submitted' || a.status === 'released';
}

export function SurveyCard({ a, label, due, fillOpen = true }: { a: ReviewAssignmentSummary; label: string; due?: string | null; fillOpen?: boolean }) {
  const done = isSurveyDone(a);
  const pct = a.total > 0 ? Math.round((a.answered / a.total) * 100) : 0;
  const status = done
    ? 'Submitted ✓'
    : !fillOpen
      ? 'Filling is closed'
      : a.answered === 0
        ? 'Not started'
        : a.total > 0 && a.answered >= a.total
          ? 'Ready to submit'
          : `${a.answered} of ${a.total} answered`;
  const verb = done || !fillOpen ? 'Open' : a.answered === 0 ? 'Start' : a.total > 0 && a.answered >= a.total ? 'Review & submit' : 'Continue';
  return (
    <Link to={`/performance-reviews/${encodeURIComponent(a.id)}`} className="block rounded-lg border border-line bg-surface p-4 transition-colors hover:border-line-strong hover:bg-surface-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="text-sm font-medium text-fg">{label}</span>
          <div className="mt-0.5 text-2xs text-fg-faint">
            {a.total > 0 ? `${a.total} question${a.total === 1 ? '' : 's'}` : ''}
            {!done && due ? ` · due ${due}` : ''}
            {done ? ' · locked' : ''}
          </div>
        </div>
        <span className="flex items-center gap-2 shrink-0">
          <span className={`text-xs ${done ? 'text-success' : !fillOpen ? 'text-fg-faint' : a.answered === 0 ? 'text-fg-faint' : 'text-warning'}`}>{status}</span>
          <span className={`text-xs rounded-md px-2.5 py-1 ${done || !fillOpen ? 'border border-line-strong text-fg' : 'bg-primary text-primary-fg'}`}>{verb}</span>
        </span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-surface-3 overflow-hidden">
        <div className={`h-full ${done ? 'bg-success' : 'bg-primary'}`} style={{ width: `${done ? 100 : pct}%` }} />
      </div>
    </Link>
  );
}

export function MobileRow({ a, label, to }: { a: ReviewAssignmentSummary; label: string; to: string }) {
  return (
    <Link to={to} className="block rounded-lg bg-surface-2 ring-1 ring-line p-3">
      <div className="text-sm font-medium text-fg">{label}</div>
      <div className="text-xs text-fg-faint mt-1">{a.cycleName} · {STATUS_LABEL[a.status] ?? a.status}</div>
    </Link>
  );
}

/** Fired on window when a background review submit finishes, so an already-loaded list refreshes. */
export const REVIEWS_CHANGED_EVENT = 'daaily:reviews-changed';
